// src/pages/alianzas/CertificacionesPage.js
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Award,
  BookOpen,
  Edit2,
  ExternalLink,
  GraduationCap,
  LayoutDashboard,
  Loader2,
  Plus,
  RefreshCw,
  Search,
  Trash2,
  Users,
  ListChecks,
} from 'lucide-react';

import { useAuth } from '../../contexts/AuthContext';
import { certificacionesApi, apiErrorMessage } from '../../services/certificacionesApi';
import CertificacionesDashboard from '../../components/alianzas/CertificacionesDashboard';
import {
  ACCENT, BORDER, BTN_PRIMARY, CARD, FABRICANTE_COLORS, PAGE_BG, tint,
  Badge, ConfirmDialog, Field, ModalShell, Toast,
  fmtFecha, inputCls, selectCls,
} from '../../components/alianzas/ui';

const PAGE_SIZE = 25;

const FABRICANTE_BADGE = Object.fromEntries(
  Object.entries(FABRICANTE_COLORS).map(([nombre, hex]) => [nombre, { bg: `${hex}26`, color: hex }])
);

const NIVEL_BADGE = {
  Fundamentals: { bg: 'rgba(148,163,184,0.18)', color: '#94a3b8' },
  Associate:    { bg: 'rgba(56,189,248,0.18)',  color: '#38bdf8' },
  Professional: { bg: 'rgba(74,222,128,0.18)',  color: '#4ade80' },
  Expert:       { bg: 'rgba(250,204,21,0.18)',  color: '#facc15' },
  Specialty:    { bg: 'rgba(192,132,252,0.18)', color: '#c084fc' },
};

const ESTATUS_BADGE = {
  'En Proceso': { bg: 'rgba(56,189,248,0.18)',  color: '#38bdf8', label: 'En proceso' },
  Finalizado:   { bg: 'rgba(74,222,128,0.18)',  color: '#4ade80', label: 'Finalizado' },
  Cancelado:    { bg: 'rgba(148,163,184,0.18)', color: '#94a3b8', label: 'Cancelado' },
};

const PESTANAS = [
  { id: 'examenes',      label: 'Exámenes',      icon: Award },
  { id: 'cursos',        label: 'Cursos',        icon: BookOpen },
  { id: 'catalogo',      label: 'Catálogo',      icon: GraduationCap },
  { id: 'colaboradores', label: 'Colaboradores', icon: Users },
];

// ─── helpers ─────────────────────────────────────────────────────────────────

const pct = (v) => (v == null ? '—' : `${Math.round(Number(v) * 100)}%`);

function Kpi({ label, valor, tono }) {
  return (
    <div className={`${CARD} px-4 py-3`}>
      <p className="text-[11px] uppercase tracking-wide text-white/40">{label}</p>
      <p className="text-xl font-bold mt-0.5" style={{ color: tono || '#fff' }}>
        {valor ?? '—'}
      </p>
    </div>
  );
}

function Paginacion({ total, offset, onOffset }) {
  const paginas = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const actual = Math.floor(offset / PAGE_SIZE) + 1;
  if (paginas <= 1) return null;
  return (
    <div className="flex items-center justify-between mt-4 text-xs text-white/50">
      <span>Página {actual} de {paginas} · {total} registros</span>
      <div className="flex gap-2">
        <button
          disabled={offset === 0}
          onClick={() => onOffset(Math.max(0, offset - PAGE_SIZE))}
          className="px-3 py-1.5 rounded-lg border border-white/10 disabled:opacity-30 hover:text-white transition-colors"
        >
          Anterior
        </button>
        <button
          disabled={actual >= paginas}
          onClick={() => onOffset(offset + PAGE_SIZE)}
          className="px-3 py-1.5 rounded-lg border border-white/10 disabled:opacity-30 hover:text-white transition-colors"
        >
          Siguiente
        </button>
      </div>
    </div>
  );
}

function Tabla({ columnas, filas, loading, vacio, children }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-wide text-white/40 border-b border-white/10">
            {columnas.map(c => (
              <th key={c.key} className={`py-2 px-3 font-medium ${c.align === 'right' ? 'text-right' : ''}`}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading && (
            <tr>
              <td colSpan={columnas.length} className="py-8 text-center text-white/40">
                <Loader2 size={18} className="animate-spin inline mr-2" /> Cargando…
              </td>
            </tr>
          )}
          {!loading && filas.length === 0 && (
            <tr>
              <td colSpan={columnas.length} className="py-8 text-center text-white/40">{vacio}</td>
            </tr>
          )}
          {!loading && children}
        </tbody>
      </table>
    </div>
  );
}

function Acciones({ canEdit, canDelete, onEdit, onDelete }) {
  return (
    <div className="flex items-center justify-end gap-2">
      {canEdit && (
        <button onClick={onEdit} title="Editar"
          className="p-1.5 rounded-lg text-white/40 hover:text-sky-300 transition-colors">
          <Edit2 size={14} />
        </button>
      )}
      {canDelete && (
        <button onClick={onDelete} title="Eliminar"
          className="p-1.5 rounded-lg text-white/40 hover:text-red-400 transition-colors">
          <Trash2 size={14} />
        </button>
      )}
    </div>
  );
}

// ─── modal generico basado en definicion de campos ───────────────────────────

function FormModal({ titulo, icono, campos, inicial, onClose, onSave }) {
  const [form, setForm] = useState(() => {
    const base = {};
    campos.forEach(c => { base[c.name] = inicial?.[c.name] ?? c.default ?? ''; });
    return base;
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const change = (name, value) => setForm(f => ({ ...f, [name]: value }));

  const submit = async (e) => {
    e.preventDefault();
    const faltante = campos.find(c => c.required && !String(form[c.name] ?? '').trim());
    if (faltante) {
      setError(`${faltante.label} es obligatorio.`);
      return;
    }
    setSaving(true);
    setError('');
    try {
      const payload = {};
      campos.forEach(c => {
        let v = form[c.name];
        if (v === '' || v === undefined) v = null;
        else if (c.type === 'number' && v !== null) v = Number(v);
        else if (c.type === 'checkbox') v = Boolean(v);
        payload[c.name] = v;
      });
      await onSave(payload);
    } catch (err) {
      setError(apiErrorMessage(err, 'Error al guardar'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell title={titulo} icon={icono} onClose={onClose} wide>
      <form onSubmit={submit} className="p-6 grid grid-cols-2 gap-4 max-h-[70vh] overflow-y-auto">
        {campos.map(c => (
          <div key={c.name} className={c.ancho === 2 ? 'col-span-2' : ''}>
            <Field label={c.label} required={c.required}>
              {c.type === 'select' ? (
                <select className={selectCls} value={form[c.name] ?? ''}
                  onChange={e => change(c.name, e.target.value)}>
                  <option value="">Sin definir</option>
                  {(c.options || []).map(o => (
                    <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>
                  ))}
                </select>
              ) : c.type === 'checkbox' ? (
                <input type="checkbox" className="accent-sky-400 w-4 h-4"
                  checked={Boolean(form[c.name])}
                  onChange={e => change(c.name, e.target.checked)} />
              ) : c.type === 'file' ? (
                <input type="file" accept={c.accept} className={inputCls}
                  onChange={e => change(c.name, e.target.files?.[0] || null)} />
              ) : c.type === 'textarea' ? (
                <textarea rows={3} className={`${inputCls} resize-none`}
                  value={form[c.name] ?? ''} onChange={e => change(c.name, e.target.value)} />
              ) : (
                <input type={c.type || 'text'} step={c.step} className={inputCls}
                  value={form[c.name] ?? ''} placeholder={c.hint}
                  disabled={c.disabled}
                  onChange={e => change(c.name, e.target.value)} />
              )}
            </Field>
          </div>
        ))}

        {error && (
          <div className="col-span-2 flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-red-300 bg-red-500/10 border border-red-500/20">
            {error}
          </div>
        )}

        <div className="col-span-2 flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm border border-white/15 text-white/60 hover:text-white transition-colors">
            Cancelar
          </button>
          <button type="submit" disabled={saving} style={BTN_PRIMARY}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all disabled:opacity-50">
            {saving && <Loader2 size={13} className="animate-spin" />}
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
        </div>
      </form>
    </ModalShell>
  );
}

// ─── Pagina ──────────────────────────────────────────────────────────────────

export default function CertificacionesPage() {
  const { hasPermission } = useAuth();
  const canCreate = hasPermission('alianzas.certificaciones.create');
  const canEdit = hasPermission('alianzas.certificaciones.edit');
  const canDelete = hasPermission('alianzas.certificaciones.delete');
  const [vista, setVista] = useState('dashboard');

  const [pestana, setPestana] = useState('examenes');
  const [toast, setToast] = useState(null);
  const showToast = useCallback((msg, type = 'ok') => setToast({ msg, type }), []);

  const [enums, setEnums] = useState({
    fabricantes: [], categorias: [], areas: [], secciones: [],
    niveles: [], resultados: [], estatus_curso: [], tipos_examen: [], tipos_reconocimiento: [],
  });
  const [resumen, setResumen] = useState({});
  const [catalogoOpciones, setCatalogoOpciones] = useState([]);
  const [colaboradorOpciones, setColaboradorOpciones] = useState([]);

  const [datos, setDatos] = useState({ data: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [filtros, setFiltros] = useState({ search: '', fabricante: '', extra: '' });

  const [modal, setModal] = useState(null);
  const [borrar, setBorrar] = useState(null);
  const [borrando, setBorrando] = useState(false);

  useEffect(() => {
    certificacionesApi.getEnums().then(setEnums).catch(() => {});
    certificacionesApi.getResumen().then(setResumen).catch(() => {});
    certificacionesApi.listCatalogo({ limit: 1000 })
      .then(r => setCatalogoOpciones(r.data || [])).catch(() => {});
    certificacionesApi.listColaboradores({ limit: 2000 })
      .then(r => setColaboradorOpciones(r.data || [])).catch(() => {});
  }, []);

  // Cambiar de pestana reinicia paginacion y filtros.
  useEffect(() => {
    setOffset(0);
    setFiltros({ search: '', fabricante: '', extra: '' });
  }, [pestana]);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset };
      if (filtros.search) params.search = filtros.search;
      if (filtros.fabricante) params.fabricante = [filtros.fabricante];

      let res;
      if (pestana === 'examenes') {
        if (filtros.extra === 'vigentes' || filtros.extra === 'vencidas') params.vigencia = filtros.extra;
        if (filtros.extra === 'activos' || filtros.extra === 'inactivos') params.estado = filtros.extra;
        res = await certificacionesApi.listExamenes(params);
      } else if (pestana === 'cursos') {
        if (filtros.extra) params.estatus = [filtros.extra];
        res = await certificacionesApi.listCursos(params);
      } else if (pestana === 'catalogo') {
        if (filtros.extra) params.nivel = [filtros.extra];
        res = await certificacionesApi.listCatalogo(params);
      } else {
        delete params.fabricante;
        if (filtros.extra) params.area = [filtros.extra];
        res = await certificacionesApi.listColaboradores(params);
      }
      setDatos({ data: res.data || [], total: res.total || 0 });
    } catch (err) {
      showToast(apiErrorMessage(err, 'Error cargando la información'), 'error');
      setDatos({ data: [], total: 0 });
    } finally {
      setLoading(false);
    }
  }, [pestana, offset, filtros]);

  useEffect(() => { cargar(); }, [cargar]);

  const refrescarResumen = () => certificacionesApi.getResumen().then(setResumen).catch(() => {});

  // ── definicion de campos por pestana ───────────────────────────────────────
  const opcionesColaborador = useMemo(
    () => colaboradorOpciones.map(c => ({ value: c.correo, label: `${c.nombre} (${c.correo})` })),
    [colaboradorOpciones]
  );
  const opcionesExamen = useMemo(
    () => catalogoOpciones.map(c => ({ value: c.examen_id, label: c.nombre })),
    [catalogoOpciones]
  );

  const camposPorPestana = {
    catalogo: [
      { name: 'nombre', label: 'Nombre del examen', required: true, ancho: 2 },
      { name: 'fabricante', label: 'Fabricante', type: 'select', options: enums.fabricantes },
      { name: 'nivel', label: 'Nivel', type: 'select', options: enums.niveles },
      { name: 'categoria', label: 'Categoría', type: 'select', options: enums.categorias },
      { name: 'tipo_reconocimiento', label: 'Tipo de reconocimiento', type: 'select', options: enums.tipos_reconocimiento },
      { name: 'activo', label: 'Activo', type: 'checkbox', default: true },
    ],
    colaboradores: [
      { name: 'correo', label: 'Correo', required: true, ancho: 2, hint: 'usuario@kio.tech' },
      { name: 'nombre', label: 'Nombre', ancho: 2 },
      { name: 'puesto', label: 'Puesto' },
      { name: 'area', label: 'Área', type: 'select', options: enums.areas },
      { name: 'seccion', label: 'Sección', type: 'select', options: enums.secciones },
      { name: 'equipo', label: 'Equipo' },
      { name: 'supervisor', label: 'Supervisor' },
      { name: 'ubicacion', label: 'Ubicación' },
      { name: 'division', label: 'División' },
      { name: 'departamento', label: 'Departamento' },
      { name: 'activo', label: 'Activo', type: 'checkbox', default: true },
    ],
    examenes: [
      { name: 'correo', label: 'Colaborador', required: true, type: 'select', options: opcionesColaborador, ancho: 2 },
      { name: 'examen_id', label: 'Examen del catálogo', type: 'select', options: opcionesExamen, ancho: 2 },
      { name: 'tipo', label: 'Tipo', type: 'select', options: enums.tipos_examen },
      { name: 'resultado', label: 'Resultado', type: 'select', options: enums.resultados },
      { name: 'fecha_examen', label: 'Fecha de examen', type: 'date' },
      { name: 'fecha_expiracion', label: 'Fecha de expiración', type: 'date' },
      { name: 'puntaje', label: 'Puntaje', type: 'number' },
      { name: 'activo', label: 'Certificado activo', type: 'checkbox', default: true },
      { name: 'anio', label: 'Año', type: 'number' },
      { name: 'trimestre', label: 'Trimestre', type: 'select', options: ['Q1', 'Q2', 'Q3', 'Q4'] },
      { name: 'codigo_voucher', label: 'Código de voucher' },
      { name: 'voucher_entregado_por', label: 'Voucher entregado por' },
      { name: 'odc', label: 'ODC' },
      { name: 'comprobante_url', label: 'Comprobante (URL)', ancho: 2, hint: 'Enlace al certificado' },
      { name: 'comprobante_archivo', label: 'Subir certificado PDF', type: 'file', accept: 'application/pdf', ancho: 2 },
    ],
    cursos: [
      { name: 'correo', label: 'Colaborador', required: true, type: 'select', options: opcionesColaborador, ancho: 2 },
      { name: 'programa', label: 'Nombre del programa', required: true, ancho: 2 },
      { name: 'fabricante', label: 'Fabricante', type: 'select', options: enums.fabricantes },
      { name: 'estatus', label: 'Estatus', type: 'select', options: enums.estatus_curso, default: 'En Proceso' },
      { name: 'fecha_inicio', label: 'Fecha inicio', type: 'date' },
      { name: 'fecha_fin', label: 'Fecha fin', type: 'date' },
      { name: 'cursos_incluidos', label: 'Cursos incluidos', type: 'number', step: '0.1' },
      { name: 'cursos_completados', label: 'Cursos completados', type: 'number', step: '0.1' },
      { name: 'tipo_examen', label: 'Tipo de examen' },
      { name: 'anio', label: 'Año', type: 'number' },
      { name: 'objetivo', label: 'Objetivo', type: 'textarea', ancho: 2 },
      { name: 'notas', label: 'Notas', type: 'textarea', ancho: 2 },
    ],
  };

  const guardar = async (payload) => {
    const esEdicion = modal.mode === 'edit';
    if (pestana === 'catalogo') {
      esEdicion
        ? await certificacionesApi.updateCatalogo(modal.item.examen_id, payload)
        : await certificacionesApi.createCatalogo(payload);
    } else if (pestana === 'colaboradores') {
      if (esEdicion) {
        const { correo, ...resto } = payload;
        await certificacionesApi.updateColaborador(modal.item.correo, resto);
      } else {
        await certificacionesApi.createColaborador(payload);
      }
    } else if (pestana === 'examenes') {
      const archivo = payload.comprobante_archivo;
      delete payload.comprobante_archivo;
      if (archivo) {
        if (!payload.examen_id) throw new Error('Selecciona el examen antes de subir el certificado.');
        Object.assign(payload, await certificacionesApi.uploadComprobante(payload.examen_id, archivo));
      }
      esEdicion
        ? await certificacionesApi.updateExamen(modal.item.examen_presentado_id, payload)
        : await certificacionesApi.createExamen(payload);
    } else {
      esEdicion
        ? await certificacionesApi.updateCurso(modal.item.curso_id, payload)
        : await certificacionesApi.createCurso(payload);
    }
    showToast(esEdicion ? 'Registro actualizado' : 'Registro creado');
    setModal(null);
    setOffset(0);
    cargar();
    refrescarResumen();
  };

  const confirmarBorrado = async () => {
    setBorrando(true);
    try {
      if (pestana === 'catalogo') await certificacionesApi.deleteCatalogo(borrar.examen_id);
      else if (pestana === 'colaboradores') await certificacionesApi.deleteColaborador(borrar.correo);
      else if (pestana === 'examenes') await certificacionesApi.deleteExamen(borrar.examen_presentado_id);
      else await certificacionesApi.deleteCurso(borrar.curso_id);
      showToast('Registro eliminado');
      setBorrar(null);
      cargar();
      refrescarResumen();
    } catch (err) {
      showToast(apiErrorMessage(err, 'No se pudo eliminar'), 'error');
    } finally {
      setBorrando(false);
    }
  };

  const etiquetaBorrado = borrar
    ? borrar.nombre || borrar.programa || borrar.examen_nombre || borrar.correo
    : '';

  // ── filtros secundarios por pestana ────────────────────────────────────────
  const filtroExtra = {
    examenes: { label: 'Estado', options: [
      { value: 'activos', label: 'Activos' },
      { value: 'inactivos', label: 'Inactivos' },
      { value: 'vigentes', label: 'Vigentes por fecha' },
      { value: 'vencidas', label: 'Vencidas por fecha' },
    ] },
    cursos: { label: 'Estatus', options: (enums.estatus_curso || []).map(e => ({ value: e, label: e })) },
    catalogo: { label: 'Nivel', options: (enums.niveles || []).map(n => ({ value: n, label: n })) },
    colaboradores: { label: 'Área', options: (enums.areas || []).map(a => ({ value: a, label: a })) },
  }[pestana];

  return (
    <div className="p-6 space-y-6 min-h-screen" style={{ background: PAGE_BG, backgroundAttachment: 'fixed' }}>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Award size={22} style={{ color: ACCENT }} />
            Certificaciones
          </h1>
          <p className="text-sm text-white/50 mt-1">
            Catálogo de exámenes, colaboradores, certificaciones presentadas y cursos de IT Services.
          </p>
        </div>
        <div className="inline-flex rounded-lg border border-sky-400/20 bg-[#061a2b]/70 p-1 shrink-0">
          {[
            { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
            { id: 'gestion', label: 'Gestión', icon: ListChecks },
          ].map(item => {
            const Icon = item.icon;
            const activa = vista === item.id;
            return (
              <button key={item.id} onClick={() => setVista(item.id)}
                className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-all"
                style={{ background: activa ? tint(.2) : 'transparent', color: activa ? ACCENT : 'rgba(255,255,255,.5)' }}>
                <Icon size={15} /> {item.label}
              </button>
            );
          })}
        </div>
      </header>

      {vista === 'dashboard' ? <CertificacionesDashboard /> : <>

      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
        <Kpi label="Colaboradores" valor={resumen.colaboradores} />
        <Kpi label="Certificaciones vigentes" valor={resumen.certificaciones_vigentes} tono="#4ade80" />
        <Kpi label="Vencidas" valor={resumen.certificaciones_vencidas} tono="#f87171" />
        <Kpi label="Inactivas" valor={resumen.certificaciones_inactivas} tono="#94a3b8" />
        <Kpi label="Exámenes registrados" valor={resumen.examenes_presentados} />
        <Kpi label="Cursos en proceso" valor={resumen.cursos_en_proceso} tono={ACCENT} />
      </div>

      {/* ── pestanas ─────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap gap-2">
        {PESTANAS.map(p => {
          const Icono = p.icon;
          const activa = pestana === p.id;
          return (
            <button
              key={p.id}
              onClick={() => setPestana(p.id)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-all"
              style={{
                background: activa ? tint(0.18) : 'rgba(255,255,255,0.04)',
                border: `1px solid ${activa ? BORDER : 'rgba(255,255,255,0.08)'}`,
                color: activa ? ACCENT : 'rgba(255,255,255,0.6)',
              }}
            >
              <Icono size={15} /> {p.label}
            </button>
          );
        })}
      </div>

      <section className={`${CARD} p-5`}>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-lg font-semibold text-white">
            {PESTANAS.find(p => p.id === pestana)?.label}
            <span className="ml-2 text-sm font-normal text-white/40">{datos.total}</span>
          </h2>
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
              <input
                className={`${inputCls} pl-8 w-56`}
                placeholder="Buscar…"
                value={filtros.search}
                onChange={e => { setFiltros(f => ({ ...f, search: e.target.value })); setOffset(0); }}
              />
            </div>
            {pestana !== 'colaboradores' && (
              <select
                className={selectCls}
                value={filtros.fabricante}
                onChange={e => { setFiltros(f => ({ ...f, fabricante: e.target.value })); setOffset(0); }}
              >
                <option value="">Todos los fabricantes</option>
                {(enums.fabricantes || []).map(f => <option key={f} value={f}>{f}</option>)}
              </select>
            )}
            {filtroExtra && (
              <select
                className={selectCls}
                value={filtros.extra}
                onChange={e => { setFiltros(f => ({ ...f, extra: e.target.value })); setOffset(0); }}
              >
                <option value="">Todos · {filtroExtra.label}</option>
                {filtroExtra.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            )}
            <button onClick={cargar} title="Refrescar"
              className="p-2 rounded-lg border border-white/10 text-white/50 hover:text-white transition-colors">
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            </button>
            {canCreate && (
              <button
                onClick={() => setModal({ mode: 'create' })}
                style={BTN_PRIMARY}
                className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all"
              >
                <Plus size={14} /> Nuevo
              </button>
            )}
          </div>
        </div>

        {/* ── EXAMENES ───────────────────────────────────────────────────── */}
        {pestana === 'examenes' && (
          <Tabla
            loading={loading}
            filas={datos.data}
            vacio="No hay exámenes con los filtros seleccionados."
            columnas={[
              { key: 'colab', label: 'Colaborador' },
              { key: 'examen', label: 'Examen' },
              { key: 'fab', label: 'Fabricante' },
              { key: 'fecha', label: 'Presentado' },
              { key: 'exp', label: 'Expira' },
              { key: 'res', label: 'Resultado' },
              { key: 'activo', label: 'Activo' },
              { key: 'comp', label: 'Certificado' },
              { key: 'acc', label: 'Acciones', align: 'right' },
            ]}
          >
            {datos.data.map(e => (
              <tr key={e.examen_presentado_id} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                <td className="py-3 px-3">
                  <div className="text-white font-medium">{e.colaborador_nombre || '—'}</div>
                  <div className="text-[11px] text-white/40">{e.correo}</div>
                </td>
                <td className="py-3 px-3 text-white/80">{e.examen_nombre || '—'}</td>
                <td className="py-3 px-3"><Badge value={e.fabricante} map={FABRICANTE_BADGE} /></td>
                <td className="py-3 px-3 text-white/60 text-xs">{fmtFecha(e.fecha_examen)}</td>
                <td className="py-3 px-3 text-xs">
                  <span style={{ color: e.vigente ? 'rgba(255,255,255,0.6)' : '#f87171' }}>
                    {fmtFecha(e.fecha_expiracion)}
                  </span>
                </td>
                <td className="py-3 px-3 text-white/60 text-xs">{e.resultado || '—'}</td>
                <td className="py-3 px-3">
                  <button
                    disabled={!canEdit || !e.colaborador_activo}
                    onClick={async () => {
                      try {
                        await certificacionesApi.updateExamen(e.examen_presentado_id, { activo: !e.activo_certificado });
                        showToast(e.activo_certificado ? 'Certificado desactivado' : 'Certificado activado');
                        cargar();
                        refrescarResumen();
                      } catch (err) {
                        showToast(apiErrorMessage(err, 'No se pudo cambiar el estado'), 'error');
                      }
                    }}
                    title={e.colaborador_activo ? 'Cambiar estado del certificado' : 'El colaborador está inactivo'}
                    className="relative w-9 h-5 rounded-full transition-colors disabled:opacity-60"
                    style={{ background: e.activo_efectivo ? tint(0.65) : 'rgba(148,163,184,0.28)' }}
                  >
                    <span
                      className="absolute top-0.5 w-4 h-4 rounded-full bg-white transition-all"
                      style={{ left: e.activo_efectivo ? 18 : 2 }}
                    />
                  </button>
                </td>
                <td className="py-3 px-3">
                  {e.comprobante_url ? (
                    <a href={e.comprobante_url} target="_blank" rel="noopener noreferrer"
                      title={e.comprobante_nombre || 'Ver certificado'}
                      className="inline-flex items-center gap-1 text-[11px] text-sky-300/80 hover:text-sky-300">
                      <ExternalLink size={12} /> Ver
                    </a>
                  ) : <span className="text-white/25 text-xs">—</span>}
                </td>
                <td className="py-3 px-3">
                  <Acciones canEdit={canEdit} canDelete={canDelete}
                    onEdit={() => setModal({ mode: 'edit', item: e })}
                    onDelete={() => setBorrar(e)} />
                </td>
              </tr>
            ))}
          </Tabla>
        )}

        {/* ── CURSOS ─────────────────────────────────────────────────────── */}
        {pestana === 'cursos' && (
          <Tabla
            loading={loading}
            filas={datos.data}
            vacio="No hay cursos con los filtros seleccionados."
            columnas={[
              { key: 'colab', label: 'Colaborador' },
              { key: 'prog', label: 'Programa' },
              { key: 'fab', label: 'Fabricante' },
              { key: 'fechas', label: 'Fechas' },
              { key: 'avance', label: 'Avance' },
              { key: 'est', label: 'Estatus' },
              { key: 'acc', label: 'Acciones', align: 'right' },
            ]}
          >
            {datos.data.map(c => (
              <tr key={c.curso_id} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                <td className="py-3 px-3">
                  <div className="text-white font-medium">{c.colaborador_nombre || '—'}</div>
                  <div className="text-[11px] text-white/40">{c.correo}</div>
                </td>
                <td className="py-3 px-3">
                  <div className="text-white/80">{c.programa || '—'}</div>
                  {c.sesion_id && (
                    <span className="text-[10px] uppercase tracking-wide" style={{ color: ACCENT }}>
                      en calendario
                    </span>
                  )}
                </td>
                <td className="py-3 px-3"><Badge value={c.fabricante} map={FABRICANTE_BADGE} /></td>
                <td className="py-3 px-3 text-white/60 text-xs">
                  {fmtFecha(c.fecha_inicio)} → {fmtFecha(c.fecha_fin)}
                </td>
                <td className="py-3 px-3 text-white/60 text-xs">{pct(c.porcentaje)}</td>
                <td className="py-3 px-3"><Badge value={c.estatus} map={ESTATUS_BADGE} /></td>
                <td className="py-3 px-3">
                  <Acciones canEdit={canEdit} canDelete={canDelete}
                    onEdit={() => setModal({ mode: 'edit', item: c })}
                    onDelete={() => setBorrar(c)} />
                </td>
              </tr>
            ))}
          </Tabla>
        )}

        {/* ── CATALOGO ───────────────────────────────────────────────────── */}
        {pestana === 'catalogo' && (
          <Tabla
            loading={loading}
            filas={datos.data}
            vacio="No hay exámenes en el catálogo con esos filtros."
            columnas={[
              { key: 'nom', label: 'Examen' },
              { key: 'fab', label: 'Fabricante' },
              { key: 'niv', label: 'Nivel' },
              { key: 'cat', label: 'Categoría' },
              { key: 'tipo', label: 'Reconocimiento' },
              { key: 'act', label: 'Estado' },
              { key: 'acc', label: 'Acciones', align: 'right' },
            ]}
          >
            {datos.data.map(c => (
              <tr key={c.examen_id} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                <td className="py-3 px-3 text-white font-medium">{c.nombre}</td>
                <td className="py-3 px-3"><Badge value={c.fabricante} map={FABRICANTE_BADGE} /></td>
                <td className="py-3 px-3"><Badge value={c.nivel} map={NIVEL_BADGE} /></td>
                <td className="py-3 px-3 text-white/60 text-xs">{c.categoria || '—'}</td>
                <td className="py-3 px-3 text-white/60 text-xs">{c.tipo_reconocimiento || '—'}</td>
                <td className="py-3 px-3 text-xs" style={{ color: c.activo ? '#4ade80' : '#94a3b8' }}>
                  {c.activo ? 'Activo' : 'Inactivo'}
                </td>
                <td className="py-3 px-3">
                  <Acciones canEdit={canEdit} canDelete={canDelete}
                    onEdit={() => setModal({ mode: 'edit', item: c })}
                    onDelete={() => setBorrar(c)} />
                </td>
              </tr>
            ))}
          </Tabla>
        )}

        {/* ── COLABORADORES ──────────────────────────────────────────────── */}
        {pestana === 'colaboradores' && (
          <Tabla
            loading={loading}
            filas={datos.data}
            vacio="No hay colaboradores con esos filtros."
            columnas={[
              { key: 'nom', label: 'Colaborador' },
              { key: 'puesto', label: 'Puesto' },
              { key: 'area', label: 'Área' },
              { key: 'seccion', label: 'Sección' },
              { key: 'sup', label: 'Supervisor' },
              { key: 'act', label: 'Estado' },
              { key: 'acc', label: 'Acciones', align: 'right' },
            ]}
          >
            {datos.data.map(c => (
              <tr key={c.correo} className="border-b border-white/5 hover:bg-white/[0.03] transition-colors">
                <td className="py-3 px-3">
                  <div className="text-white font-medium">{c.nombre || '—'}</div>
                  <div className="text-[11px] text-white/40">{c.correo}</div>
                </td>
                <td className="py-3 px-3 text-white/60 text-xs">{c.puesto || '—'}</td>
                <td className="py-3 px-3 text-white/60 text-xs">{c.area || '—'}</td>
                <td className="py-3 px-3 text-white/60 text-xs">{c.seccion || '—'}</td>
                <td className="py-3 px-3 text-white/60 text-xs">{c.supervisor || '—'}</td>
                <td className="py-3 px-3 text-xs" style={{ color: c.activo ? '#4ade80' : '#94a3b8' }}>
                  {c.activo ? 'Activo' : 'Inactivo'}
                </td>
                <td className="py-3 px-3">
                  <Acciones canEdit={canEdit} canDelete={canDelete}
                    onEdit={() => setModal({ mode: 'edit', item: c })}
                    onDelete={() => setBorrar(c)} />
                </td>
              </tr>
            ))}
          </Tabla>
        )}

        <Paginacion total={datos.total} offset={offset} onOffset={setOffset} />
      </section>

      {modal && (
        <FormModal
          titulo={`${modal.mode === 'create' ? 'Nuevo' : 'Editar'} · ${PESTANAS.find(p => p.id === pestana)?.label}`}
          icono={<Award size={18} style={{ color: ACCENT }} />}
          campos={camposPorPestana[pestana].map(c =>
            modal.mode === 'edit' && (c.name === 'correo' && pestana === 'colaboradores')
              ? { ...c, disabled: true }
              : c
          )}
          inicial={modal.item}
          onClose={() => setModal(null)}
          onSave={guardar}
        />
      )}

      {borrar && (
        <ConfirmDialog
          title="Eliminar registro"
          message={
            pestana === 'colaboradores' || pestana === 'catalogo'
              ? `"${etiquetaBorrado}" se marcará como inactivo para no perder su historial.`
              : `¿Confirmas eliminar "${etiquetaBorrado}"? Esta acción no se puede deshacer.`
          }
          confirmLabel={borrando ? 'Eliminando…' : 'Eliminar'}
          busy={borrando}
          onClose={() => setBorrar(null)}
          onConfirm={confirmarBorrado}
        />
      )}
      </>}

      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
    </div>
  );
}
